import "@tento-ui/components/grid/grid-layout";
import "@tento-ui/components/round";
import "@tento-ui/components/documentation/documentation";
import "@tento-ui/components/section-head";

import { html, LonaWebComponent } from "@tento-ui/component";
import { Column, Div } from "@tento-ui/component-builtin";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { GridLayout } from "@tento-ui/components/grid/grid-layout";
import { Documentation } from "@tento-ui/components/documentation/documentation";
import { SectionHead } from "@tento-ui/components/section-head";
import { DomUtils } from "@tento-ui/dom";
import { $$ } from "@tento-ui/fastdom";
import { Storybook } from "@tento-ui/ui/storybook";
import { Round } from "@tento-ui/components/round";
import { Button } from "@tento-ui/components/button/button";
import { ThemeContext } from "@tento-ui/components/theme-context/theme-context";
import { RichTextEditor } from "@tento-ui/components/rich-text-editor/rich-text-editor";
import { Typography } from "@tento-ui/ui/typography";

export namespace StorybookContainer {
  export type Section = {
    id: string;
    label: string;
    gridConfig: GridLayout.Config;
    items: Storybook[];
    /** Admitted tags nobody has drawn yet. Each renders as a dimmed card so a
     * hole in the library is visible rather than silent. */
    gaps: string[];
  };
  export type Props = {
    sections: Section[];
  };
}

// One section = a head and its own masonry grid. The grid absolutely positions
// its children at column width and detaches the off-screen ones, so a
// full-width heading cannot live inside it — sectioning is N grids under ONE
// expand overlay, and the container keeps its own card arrays because
// `grid.children` is not a stable index.
type Mounted = {
  id: string;
  $section: HTMLElement;
  gridConfig: GridLayout.Config;
  $grid: Option<GridLayout>;
  $cards: Storybook.Card[];
};

@component({
  name: "std-storybook-container",
})
export class StorybookContainer extends LonaWebComponent {
  private $sections: HTMLElement = this.$("sections");
  private $expandedGrid: GridLayout = this.hydrate("expanded-grid");
  private $expandedDescription: ThemeContext = this.hydrate(
    "expanded-description",
  );
  private $previousExpanded: Option<Storybook.Card>;
  private expandedResizeObserver: Option<ResizeObserver>;
  private currentExpandedCard: Option<Storybook.Card>;
  private _$expandedBg = this.hydrate<Round>("expanded-bg")
    .bind({
      ...Round.Props.MODAL,
      bg: "#444",
    });

  private mounted: Map<string, Mounted> = new Map();
  private gridOf: WeakMap<Storybook.Card, GridLayout> = new WeakMap();
  private bindGeneration = 0;
  private expansionGeneration = 0;

  disconnectedCallback() {
    ++this.bindGeneration;
    this.resetExpanded();
    super.disconnectedCallback();
  }

  scrollToSection(id: string) {
    this.mounted.get(id)?.$section.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }

  scrollToCard(sectionId: string, index: number) {
    const m = this.mounted.get(sectionId);
    const $card = m?.$cards[index];
    if (!m || !$card) return;
    if (!$card.isConnected) {
      // Virtualised out. Bring the grid on screen, let it re-pin, try once more.
      m.$grid?.scrollIntoView({ behavior: "smooth", block: "start" });
      requestAnimationFrame(() => {
        if ($card.isConnected) this.flash($card);
      });
      return;
    }
    this.flash($card);
  }

  private flash($card: HTMLElement) {
    $card.scrollIntoView({ behavior: "smooth", block: "center" });
    $card.toggleAttribute("flash", true);
    setTimeout(() => $card.removeAttribute("flash"), 800);
  }

  async bind(props: StorybookContainer.Props): Promise<this> {
    const generation = ++this.bindGeneration;
    this.resetExpanded();
    DomUtils.clearChildren(this.$sections);
    this.mounted.clear();
    this.gridOf = new WeakMap();

    for (const section of props.sections) {
      const $section = Column.make();
      $section.classList.add("section");
      $section.dataset.section = section.id;
      $section.appendChild(
        SectionHead.makeWith({
          title: section.label,
          count: section.items.length + section.gaps.length,
        }),
      );

      const stories = [
        ...section.items,
        ...section.gaps.map((tag) => StorybookContainer.gapStory(tag)),
      ];
      const m: Mounted = {
        id: section.id,
        $section,
        gridConfig: section.gridConfig,
        $grid: null,
        $cards: [],
      };
      if (stories.length === 0) {
        $section.appendChild(this.$empty());
      } else {
        const $grid = GridLayout.make();
        $grid.classList.add("grid");
        $grid.toggleAttribute("no-transition", true);
        const $cards = await Promise.all(
          stories.map(async (sb) => {
            const $card = await this.renderStorybook(sb);
            $card.style.opacity = "0";
            if (generation === this.bindGeneration) {
              this.gridOf.set($card, $grid);
            }
            return $card;
          }),
        );
        // A newer mode may have cleared these sections while stories rendered.
        if (generation !== this.bindGeneration) return this;
        for (const $card of $cards) $grid.appendChild($card);
        $section.appendChild($grid);
        m.$grid = $grid;
        m.$cards = $cards;
        $$.mutateEnd(() => {
          if (generation !== this.bindGeneration) return;
          for (const $card of $cards) $card.style.opacity = "";
        });
      }
      this.mounted.set(section.id, m);
      this.$sections.appendChild($section);
    }

    // Grids measure their own width, so they lay out once they are in the tree.
    for (const m of this.mounted.values()) m.$grid?.bind(m.gridConfig);

    return this;
  }

  private resetExpanded() {
    ++this.expansionGeneration;
    this.expandedResizeObserver?.disconnect();
    this.expandedResizeObserver = null;
    this.currentExpandedCard = null;
    DomUtils.clearChildren(this.$expandedGrid);
    if (this.$previousExpanded) {
      this.$previousExpanded.setExpanded(false);
      this.gridOf.get(this.$previousExpanded)?.toggleCellExpanded(
        this.$previousExpanded,
        false,
        0,
      );
      this.$previousExpanded = null;
    }
    for (const m of this.mounted.values()) {
      m.$grid?.removeAttribute("has-expanded");
    }
    this.$("expanded").assignStyles({
      transition: "none",
      opacity: "0",
      pointerEvents: "none",
      display: "none",
    });
  }

  /** A region with nothing in it says so. Reserved space that renders nothing
   * is a hole indistinguishable from a failed render. */
  private $empty(): HTMLElement {
    const $e = Div.make();
    $e.classList.add("empty");
    $e.appendChild(Typography.small("No components yet"));
    return $e;
  }

  /** An admitted tag with no story: a card that names the hole. */
  static gapStory(tag: string): Storybook {
    return {
      title: () => tag,
      tags: () => [],
      preview: () => ({
        title: tag,
        render: () => {
          const $gap = Div.make();
          $gap.classList.add("gap");
          $gap.appendChild(Typography.small(`${tag} — no storybook yet`));
          return $gap;
        },
      }),
      examples: () => [],
    };
  }

  private async renderStorybook(story: Storybook): Promise<Storybook.Card> {
    const $card = Storybook.Card.make();
    await $card.bind(story);
    $card.onToggleExpand = (isExpanded: boolean) =>
      this.toggleExpand($card, isExpanded);
    return $card;
  }

  private async renderExample(
    example: Storybook.Example,
  ): Promise<Storybook.Card.Layout> {
    const $container = Storybook.Card.Layout.make();
    await $container.bindExample(example);
    return $container;
  }

  private setupExpandedObserver($card: Storybook.Card, $grid: GridLayout) {
    const $expanded = this.$("expanded");
    this.expandedResizeObserver?.disconnect();
    // margin-block: 12px (top + bottom)
    const totalMargin = 24;
    this.expandedResizeObserver = new ResizeObserver(() => {
      const height = $expanded.offsetHeight + totalMargin;
      if (height > 0 && this.currentExpandedCard === $card) {
        const fold = $grid.toggleCellExpanded($card, true, height);
        // The overlay is positioned against the HOST; the grid's offsetTop is
        // host-relative only while the section columns stay unpositioned —
        // see the `.section` rule below.
        const top = $grid.offsetTop + (fold ?? 0);
        $expanded.style.transform = `translate(0px, ${top}px)`;
      }
    });
    this.expandedResizeObserver.observe($expanded);
  }

  private toggleExpand = async ($card: Storybook.Card, isExpanded: boolean) => {
    const $grid = this.gridOf.get($card);
    if (!$grid) return;
    if (!isExpanded) {
      $card.setExpanded(false);
      this.resetExpanded();
      return;
    }
    const generation = ++this.expansionGeneration;

    const examples = await Promise.all(
      $card.$expandedExamples.map((sb) => this.renderExample(sb)),
    );
    // A newer selection, mode bind or disconnect owns the overlay now.
    if (generation !== this.expansionGeneration) return;
    DomUtils.clearChildren(this.$expandedGrid);
    for (const $e of examples) {
      this.$expandedGrid.appendChild($e);
    }

    const $previous = this.$previousExpanded;
    if ($previous && $previous !== $card) {
      $previous.setExpanded(false);
      $previous.removeAttribute("expanded");
      const $previousGrid = this.gridOf.get($previous);
      if ($previousGrid && $previousGrid !== $grid) {
        $previousGrid.removeAttribute("has-expanded");
        $previousGrid.toggleCellExpanded($previous, false, 0);
      }
    }

    $grid.toggleAttribute("has-expanded", true);
    $card.setExpanded(true);

    this.currentExpandedCard = $card;

    const $expanded = this.$("expanded");
    $expanded.style.transition = "opacity 0.3s ease";
    $expanded.style.display = "flex";

    const owner = [...this.mounted.values()].find((m) => m.$grid === $grid);
    if (owner) this.$expandedGrid.bind(owner.gridConfig);
    this.setupExpandedObserver($card, $grid);

    $$.deferMutate(async () => {
      if (generation !== this.expansionGeneration) return;
      this.$expandedGrid.layoutGridSync();
      this.$("expanded-title").textContent = $card.storybook!.title();

      const description = $card.storybook?.description?.();
      if (description) {
        this.$expandedDescription.style.display = "block";
        const $docs = RichTextEditor.make();
        await $docs.bind({
          markdown: description,
          nodeViews: {},
          editable: false,
        });
        if (generation !== this.expansionGeneration) return;
        this.$expandedDescription.bind({
          theme: "dark",
          styles: [Documentation.$docStyle],
          children: [$docs],
        });
      } else {
        this.$expandedDescription.style.display = "none";
      }

      $grid.style.setProperty("--transition-duration", "0.15s");

      $expanded.assignStyles({
        opacity: "1",
        pointerEvents: "all",
      });

      $$.deferMutate(() => {
        if (generation !== this.expansionGeneration) return;
        $expanded.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });

    this.$previousExpanded = $card;
  };

  static $styles: Style.Compat[] = [
    Button.$style,
    css`
      :host {
        display: block;
        position: relative;
      }

      #sections {
        display: flex;
        flex-direction: column;
        gap: 40px;
      }

      /* Sections MUST stay unpositioned: the expand overlay is translated by
        the owning grid's offsetTop, which is host-relative only while no
        ancestor between grid and host is positioned. */
      .section {
        position: static;
        gap: 12px;
      }

      .grid {
        display: block;
        flex-shrink: 0;
      }

      std-grid-layout[has-expanded] > *:not([expanded]) {
        opacity: 0.5;
        transition: filter 0.3s ease, opacity 0.3s ease;
      }

      std-grid-layout[has-expanded] > [expanded] {
        filter: none;
        opacity: 1;
      }

      .empty {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 120px;
        border: 1px dashed var(--divider);
        border-radius: 8px;
        color: var(--text-ter);
      }

      .gap {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 80px;
        width: 100%;
        border: 1px dashed var(--divider);
        border-radius: 8px;
        color: var(--text-ter);
      }

      #expanded {
        position: absolute;
        width: 100%;
        top: 0px;
        left: 0px;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.3s ease;
        padding-block: 20px;
        margin-block: 12px;
      }

      #expanded-grid {
        display: block;
        width: 100%;
        position: relative;
      }

      #expanded-header {
        padding-block: 24px;
        padding-inline: 8px;
        z-index: 1;
        color: var(--text-sec);
      }

      #expanded-bg {
        margin-inline: -24px;
        --bg-width: calc(100% + 48px);
      }

      hr {
        margin: 0;
        height: 2px;
      }

      #expanded-title {
        color: var(--text-pri-inv);
      }

      #expanded-description {
        display: none;
        padding-inline: 8px;
        margin-bottom: 16px;
        color: var(--text-sec);
      }
    `,
  ];

  static $html = html`
    <div id="sections"></div>
    <std-col id="expanded">
      <std-round id="expanded-bg" floating></std-round>
      <std-col id="expanded-header">
        <h4 id="expanded-title"></h4>
      </std-col>
      <std-theme-context id="expanded-description"></std-theme-context>
      <std-grid-layout id="expanded-grid" no-transition></std-grid-layout>
    </std-col>
  `;
}
